import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* daily-program-item-data-store actions */
import * as DailyProgramItemDataStoreActions from 'src/app/features/data-stores/daily-program-item-data-store/state/daily-program-item-data-store.actions';

/* api services */
import { DailyProgramItemControllerService } from 'src/app/features/shared/sdk_v1/services';

export const navigatePathAfterCreatingInstance = '/daily-program-items/';
export const navigatePathAfterUpdatingInstance = '/daily-program-items/';
@Injectable()
export class DailyProgramItemDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private dailyProgramItemApiService: DailyProgramItemControllerService,
  ) {
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start

    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end

    // #endregion customCode: data-store-effects__constructor__end
  }

  GetActiveDailyProgramItemId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DailyProgramItemDataStoreActions.InitFetchingActiveDailyProgramItemWithRelations,
      ),
      withLatestFrom(
        this.store.select((state) => state.RouterDataStore.params),
      ),
      /* dataset */
      mergeMap(([action, params]) => {
        const data = {
          eventPayload: action['payload'] ?? {},
          params,
        };
        const initialPayload = of(JSONAtaObject.chain(data));
        return initialPayload;
      }),

      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__start

      // #endregion customCode: data-store-effects__pre-data-transformation__start

      /* pre data transformation */
      mergeMap(payloadAsJsonataObj => {
        /* Pre Data Transformation Step 1 ( Map ) */
        /* get value of the passed jsonata object */
        const data = <any>payloadAsJsonataObj.value;
        /* transform data */
        const transformedData = JSONAtaObject.chain(data).transform(`
              { "id": params.id }
            `);
        return transformedData;
      }),
      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__end

      // #endregion customCode: data-store-effects__pre-data-transformation__end
      /* trigger success actions */
      mergeMap((jsonataObj) => {
        /* get value of the passed jsonata object */
        const data = <any>jsonataObj.value;
        return [
          DailyProgramItemDataStoreActions.GetActiveDailyProgramItemWithRelationsById({ payload: data }),
        ];
      }),
    ),
  );

  getDailyProgramItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyProgramItemDataStoreActions.GetDailyProgramItems),
      mergeMap((action) => {
        return this.dailyProgramItemApiService.find().pipe(
          mergeMap((dailyProgramItems) => {
            const payload: any = {
              dailyProgramItems: dailyProgramItems,
            };
            return [
              DailyProgramItemDataStoreActions.GetDailyProgramItemsSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [
              DailyProgramItemDataStoreActions.GetDailyProgramItemsFailure(),
            ];
          }),
        );
      }),
    ),
  );

  getDailyProgramItemById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyProgramItemDataStoreActions.GetDailyProgramItemById),
      mergeMap((action) => {
        return this.dailyProgramItemApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((dailyProgramItem) => {
              const payload: any = {
                dailyProgramItem: dailyProgramItem,
              };
              return [
                DailyProgramItemDataStoreActions.GetDailyProgramItemByIdSuccessful(
                  { payload },
                ),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                DailyProgramItemDataStoreActions.GetDailyProgramItemByIdFailure(),
              ];
            }),
          );
      }),
    ),
  );

  getActiveDailyProgramItemWithRelationsById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DailyProgramItemDataStoreActions.GetActiveDailyProgramItemWithRelationsById,
      ),
      mergeMap((action) => {
        return this.dailyProgramItemApiService
          .findById({
            id: action.payload.id,
            filter: {
              include: ['tour'],
            },
          })
          .pipe(
            mergeMap((dailyProgramItem) => {
              const payload: any = {
                activeDailyProgramItem: dailyProgramItem,
              };
              return [
                DailyProgramItemDataStoreActions.GetActiveDailyProgramItemByIdWithRelationsSuccessful(
                  { payload },
                ),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                DailyProgramItemDataStoreActions.GetActiveDailyProgramItemByIdWithRelationsFailure(),
              ];
            }),
          );
      }),
    ),
  );

  createDailyProgramItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyProgramItemDataStoreActions.CreateDailyProgramItem),
      mergeMap((action) => {
        const params = {
          body: action.payload.dailyProgramItem,
        };
        return this.dailyProgramItemApiService.create(<any>params).pipe(
          mergeMap((dailyProgramItem) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              dailyProgramItem: dailyProgramItem,
            };
            return [
              DailyProgramItemDataStoreActions.CreateDailyProgramItemSuccessful(
                { payload },
              ),
              Navigate({
                payload: { path: navigatePathAfterCreatingInstance },
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [
              DailyProgramItemDataStoreActions.CreateDailyProgramItemFailure(),
            ];
          }),
        );
      }),
    ),
  );

  updateDailyProgramItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyProgramItemDataStoreActions.UpdateDailyProgramItem),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.dailyProgramItemApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Veri güncellendi',
              'Verileriniz başarıyla güncellendi.',
            );
            return [
              DailyProgramItemDataStoreActions.GetDailyProgramItems(),
              Navigate({
                payload: { path: navigatePathAfterUpdatingInstance },
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [
              DailyProgramItemDataStoreActions.UpdateDailyProgramItemFailure(),
            ];
          }),
        );
      }),
    ),
  );

  deleteDailyProgramItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyProgramItemDataStoreActions.DeleteDailyProgramItem),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.dailyProgramItemApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              DailyProgramItemDataStoreActions.DeleteDailyProgramItemSuccessful(
                { payload },
              ),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [
              DailyProgramItemDataStoreActions.DeleteDailyProgramItemFailure(),
            ];
          }),
        );
      }),
    ),
  );
}
